import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FaLock, FaCheck, FaQuestionCircle } from 'react-icons/fa';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus.ts';
import { useAchievementNotifications } from '../contexts/AchievementNotificationContext.tsx';
import { formatFaunderPurchased, useFaunderStats } from '../hooks/useFaunderStats.ts';
import { API_BASE } from '../utils/apiEndpoints';
import {
  BillingPeriod,
  Plan,
  PlanIcon,
  formatPrice,
  getChargeAmount,
  getDisplayPrice,
  getPlanById,
  getTotalPrice,
  isYearlyOnlyPlan,
} from '../config/subscriptionPlans.tsx';
import './css/SubscriptionCheckoutPage.css';

type PaymentMethod = 'card' | 'sbp' | 'invoice';

function interpolate(template: string, vars: Record<string, string>): string {
  return Object.entries(vars).reduce(
    (str, [key, val]) => str.replace(new RegExp(`\\{${key}\\}`, 'g'), val),
    template,
  );
}

function HeroGlobe() {
  return (
    <div className="checkout-hero-visual" aria-hidden>
      <img
        src="/home/hero-planet.png"
        alt=""
        className="checkout-hero-image"
        loading="eager"
        decoding="async"
      />
    </div>
  );
}

function formatCardNumber(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 16);
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
}

function formatExpiry(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)} / ${digits.slice(2)}`;
}

function SubscriptionCheckoutPage() {
  const { t } = useTranslation();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const { refetch: refetchSubscriptionStatus } = useSubscriptionStatus();
  const { showAchievements } = useAchievementNotifications() ?? {};
  const {
    purchasedCount: faunderPurchasedCount,
    purchaseLimit: faunderPurchaseLimit,
    alreadyPurchased: faunderOwned,
    soldOut: faunderSoldOut,
    refetch: refetchFaunderStats,
  } = useFaunderStats();
  const emailPrefilledRef = useRef(false);
  const submittingRef = useRef(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const planId = searchParams.get('plan') || 'pro';
  const plan = useMemo(() => getPlanById(planId), [planId]);
  const yearlyOnly = plan != null && isYearlyOnlyPlan(plan);
  const initialPeriod = (yearlyOnly || searchParams.get('period') === 'yearly' ? 'yearly' : 'monthly') as BillingPeriod;

  const [period, setPeriod] = useState<BillingPeriod>(initialPeriod);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!plan || (plan.monthlyPrice == null && plan.yearlyOnlyPrice == null)) {
      navigate('/subscription', { replace: true });
    }
  }, [plan, navigate]);

  useEffect(() => {
    if (yearlyOnly && period !== 'yearly') {
      setPeriod('yearly');
    }
  }, [yearlyOnly, period]);

  useEffect(() => {
    if (authLoading || emailPrefilledRef.current) return;

    const applyEmail = (value: string | undefined) => {
      if (!value?.trim() || emailPrefilledRef.current) return;
      setEmail(value.trim());
      emailPrefilledRef.current = true;
    };

    if (user?.email) {
      applyEmail(user.email);
      return;
    }

    if (!isAuthenticated) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_BASE}/api/profile/profile`, { credentials: 'include' });
        if (!response.ok || cancelled) return;
        const profile = await response.json();
        if (!cancelled) applyEmail(profile.email);
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, user?.email]);

  if (!plan || (plan.monthlyPrice == null && plan.yearlyOnlyPrice == null)) {
    return null;
  }

  const monthlyPrice = plan.monthlyPrice;
  const displayPrice = yearlyOnly ? plan.yearlyOnlyPrice! : getDisplayPrice(monthlyPrice!, period);
  const priceSuffixKey = yearlyOnly ? 'subscription.perYearSuffix' : 'subscription.perMonthSuffix';
  const subtotal = getChargeAmount(plan, period);
  const total = Math.max(0, subtotal - promoDiscount);
  const yearlySavings = monthlyPrice != null ? monthlyPrice * 12 - getTotalPrice(monthlyPrice, 'yearly') : 0;

  const handleApplyPromo = () => {
    setPromoError(null);
    if (!promoCode.trim()) return;
    if (promoCode.trim().toUpperCase() === 'SCIPLEX10') {
      setPromoDiscount(Math.round(subtotal * 0.1));
      setPromoApplied(true);
    } else {
      setPromoDiscount(0);
      setPromoApplied(false);
      setPromoError(t('subscription.checkout.promoInvalid'));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    if (!isAuthenticated) {
      setError(t('subscription.loginRequired'));
      return;
    }
    if (plan.id === 'faunder' && faunderOwned) {
      setError(t('subscription.faunder.alreadyPurchased'));
      return;
    }
    if (plan.id === 'faunder' && faunderSoldOut) {
      setError(t('subscription.faunder.soldOut'));
      return;
    }
    submittingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/subscription/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          planId: plan.id,
          period,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (data.code === 'faunder_already_purchased') {
          setError(t('subscription.faunder.alreadyPurchased'));
          await refetchFaunderStats();
          return;
        }
        if (data.code === 'faunder_sold_out') {
          setError(t('subscription.faunder.soldOut'));
          await refetchFaunderStats();
          return;
        }
        setError(data.message || t('subscription.error'));
        return;
      }
      await refetchSubscriptionStatus();
      if (plan.id === 'faunder') await refetchFaunderStats();
      if (Array.isArray(data.earned_achievements) && data.earned_achievements.length > 0 && showAchievements) {
        showAchievements(data.earned_achievements);
      }
      setSuccess(true);
      window.setTimeout(() => navigate('/problems'), 1200);
    } catch {
      setError(t('subscription.error'));
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <div className={`checkout-page ${plan.exclusive ? 'checkout-page--exclusive' : ''}`}>
      <div className="checkout-layout">
        <aside className="checkout-sidebar checkout-sidebar--left">
          <Link to="/subscription" className="checkout-back-link">
            ← {t('subscription.checkout.backToPlans')}
          </Link>

          <HeroGlobe />

          <h1 className="checkout-hero-title">{t('subscription.checkout.heroTitle')}</h1>
          <p className="checkout-hero-subtitle">
            {interpolate(t('subscription.checkout.heroSubtitle'), { plan: t(plan.nameKey) })}
          </p>

          <div className="checkout-trust-blocks">
            <article className="checkout-trust-item">
              <span className="checkout-trust-icon"><FaLock /></span>
              <div>
                <h3>{t('subscription.trust.secureTitle')}</h3>
                <p>{t('subscription.checkout.secureDesc')}</p>
              </div>
            </article>
            <article className="checkout-trust-item">
              <span className="checkout-trust-icon checkout-trust-icon--guarantee">
                <span>14</span>
              </span>
              <div>
                <h3>{t('subscription.trust.guaranteeTitle')}</h3>
                <p>{t('subscription.checkout.guaranteeDesc')}</p>
              </div>
            </article>
          </div>

          <div className="checkout-payment-badges" aria-hidden>
            <span>PCI DSS</span>
            <span>Verified by VISA</span>
            <span>Mastercard SecureCode</span>
            <span>МИР Accept</span>
          </div>
        </aside>

        <main className="checkout-main">
          <form className="checkout-form" onSubmit={handleSubmit} noValidate>
            <div className="checkout-form-header">
              <h2>{t('subscription.paymentTitle')}</h2>
              <span className="checkout-secure-badge">
                <FaLock aria-hidden />
                {t('subscription.checkout.secureConnection')}
              </span>
            </div>

            <section className="checkout-step">
              <h3 className="checkout-step-title">
                <span className="checkout-step-num">1</span>
                {t('subscription.checkout.selectedPlan')}
              </h3>
              <PlanCard plan={plan} price={displayPrice} period={period} priceSuffixKey={priceSuffixKey} t={t} />
              {yearlyOnly ? (
                <p className="checkout-alt-method-note">{t('subscription.faunder.yearlyOnly')}</p>
              ) : (
                <div className="checkout-field">
                  <label htmlFor="checkout-period">{t('subscription.periodSelector')}</label>
                  <select
                    id="checkout-period"
                    value={period}
                    onChange={(e) => {
                      setPeriod(e.target.value as BillingPeriod);
                      setPromoDiscount(0);
                      setPromoApplied(false);
                    }}
                  >
                    <option value="monthly">{t('subscription.periods.monthly')}</option>
                    <option value="yearly">{t('subscription.periods.yearly')}</option>
                  </select>
                </div>
              )}
              <Link to="/subscription#plans" className="checkout-change-plan">
                {t('subscription.checkout.changePlan')}
              </Link>
            </section>

            <section className="checkout-step">
              <h3 className="checkout-step-title">
                <span className="checkout-step-num">2</span>
                {t('subscription.checkout.contactDetails')}
              </h3>
              <div className="checkout-field-row">
                <div className="checkout-field">
                  <label htmlFor="checkout-email">{t('subscription.checkout.email')}</label>
                  <input
                    id="checkout-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@example.com"
                    autoComplete="email"
                  />
                </div>
                <div className="checkout-field">
                  <label htmlFor="checkout-name">{t('subscription.checkout.fullName')}</label>
                  <input
                    id="checkout-name"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder={t('subscription.checkout.fullNamePlaceholder')}
                    autoComplete="name"
                  />
                </div>
              </div>
            </section>

            <section className="checkout-step">
              <h3 className="checkout-step-title">
                <span className="checkout-step-num">3</span>
                {t('subscription.checkout.paymentMethod')}
              </h3>
              <div className="checkout-payment-tabs" role="tablist">
                {(['card', 'sbp', 'invoice'] as PaymentMethod[]).map((method) => (
                  <button
                    key={method}
                    type="button"
                    role="tab"
                    aria-selected={paymentMethod === method}
                    className={`checkout-payment-tab ${paymentMethod === method ? 'checkout-payment-tab--active' : ''}`}
                    onClick={() => setPaymentMethod(method)}
                  >
                    {t(`subscription.checkout.methods.${method}`)}
                  </button>
                ))}
              </div>

              {paymentMethod === 'card' && (
                <div className="checkout-card-fields">
                  <div className="checkout-field">
                    <label htmlFor="checkout-card">{t('subscription.checkout.cardNumber')}</label>
                    <div className="checkout-card-input-wrap">
                      <input
                        id="checkout-card"
                        type="text"
                        inputMode="numeric"
                        value={cardNumber}
                        onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                        placeholder="0000 0000 0000 0000"
                        autoComplete="cc-number"
                      />
                      <span className="checkout-card-brands" aria-hidden>VISA · MC</span>
                    </div>
                  </div>
                  <div className="checkout-field-row">
                    <div className="checkout-field">
                      <label htmlFor="checkout-expiry">{t('subscription.checkout.expiry')}</label>
                      <input
                        id="checkout-expiry"
                        type="text"
                        inputMode="numeric"
                        value={cardExpiry}
                        onChange={(e) => setCardExpiry(formatExpiry(e.target.value))}
                        placeholder="ММ / ГГ"
                        autoComplete="cc-exp"
                      />
                    </div>
                    <div className="checkout-field">
                      <label htmlFor="checkout-cvc">
                        {t('subscription.checkout.cvc')}
                        <FaQuestionCircle className="checkout-cvc-hint" title={t('subscription.checkout.cvcHint')} />
                      </label>
                      <input
                        id="checkout-cvc"
                        type="text"
                        inputMode="numeric"
                        value={cardCvc}
                        onChange={(e) => setCardCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        placeholder="•••"
                        autoComplete="cc-csc"
                      />
                    </div>
                  </div>
                </div>
              )}

              {paymentMethod === 'sbp' && (
                <p className="checkout-alt-method-note">{t('subscription.checkout.sbpNote')}</p>
              )}

              {paymentMethod === 'invoice' && (
                <p className="checkout-alt-method-note">{t('subscription.checkout.invoiceNote')}</p>
              )}
            </section>

            {error && <p className="checkout-form-error" role="alert">{error}</p>}
            {success && <p className="checkout-form-success" role="status">{t('subscription.success')}</p>}
          </form>
        </main>

        <aside className="checkout-sidebar checkout-sidebar--right">
          <div className="checkout-order">
            <h2 className="checkout-order-title">{t('subscription.checkout.yourOrder')}</h2>

            <div className="checkout-order-plan">
              <div className="checkout-order-plan-head">
                <PlanIcon type={plan.icon} className="checkout-order-icon" />
                <div>
                  <span className="checkout-order-plan-name">{t(plan.nameKey)}</span>
                  <span className="checkout-order-plan-price">
                    {formatPrice(displayPrice)} ₽ {t(priceSuffixKey)}
                  </span>
                </div>
              </div>
              <p className="checkout-order-period">
                {period === 'yearly'
                  ? t('subscription.checkout.yearlySubscription')
                  : t('subscription.checkout.monthlySubscription')}
              </p>
            </div>

            <dl className="checkout-order-breakdown">
              <div className="checkout-order-row">
                <dt>{t('subscription.checkout.subtotal')}</dt>
                <dd>{formatPrice(subtotal)} ₽</dd>
              </div>
              <div className="checkout-order-row">
                <dt>{t('subscription.checkout.promoDiscount')}</dt>
                <dd className={promoDiscount > 0 ? 'checkout-order-discount' : ''}>
                  {promoDiscount > 0 ? `− ${formatPrice(promoDiscount)}` : '0'} ₽
                </dd>
              </div>
              <div className="checkout-order-row checkout-order-row--total">
                <dt>{t('subscription.checkout.total')}</dt>
                <dd>
                  {formatPrice(total)} ₽
                  <span className="checkout-order-vat">{t('subscription.checkout.inclVat')}</span>
                </dd>
              </div>
            </dl>

            {period === 'monthly' && yearlySavings > 0 && (
              <div className="checkout-savings-alert">
                {interpolate(t('subscription.checkout.savingsAlert'), { amount: formatPrice(yearlySavings) })}
              </div>
            )}

            <div className="checkout-order-promo">
              <label htmlFor="checkout-promo">{t('subscription.checkout.promoCode')}</label>
              <div className="checkout-promo-row">
                <input
                  id="checkout-promo"
                  type="text"
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value);
                    setPromoApplied(false);
                    setPromoError(null);
                  }}
                  placeholder={t('subscription.checkout.promoPlaceholder')}
                />
                <button type="button" className="checkout-promo-btn" onClick={handleApplyPromo}>
                  {t('subscription.checkout.applyPromo')}
                </button>
              </div>
              {promoError && <p className="checkout-field-error">{promoError}</p>}
              {promoApplied && <p className="checkout-field-success">{t('subscription.checkout.promoApplied')}</p>}
            </div>

            {yearlyOnly && (
              <p className="checkout-faunder-count" aria-live="polite">
                {formatFaunderPurchased(
                  t('subscription.faunder.purchasedCount'),
                  faunderPurchasedCount,
                  faunderPurchaseLimit,
                )}
              </p>
            )}

            <button
              type="button"
              className="checkout-submit-btn"
              onClick={handleSubmit}
              disabled={loading || success || (yearlyOnly && (faunderOwned || faunderSoldOut))}
            >
              {yearlyOnly && faunderOwned
                ? t('subscription.faunder.alreadyOwned')
                : loading ? '...' : t('subscription.checkout.submit')}
            </button>

            <p className="checkout-legal">
              {t('subscription.checkout.legalPrefix')}{' '}
              <Link to="/terms">{t('subscription.checkout.terms')}</Link>{' '}
              {t('subscription.checkout.legalAnd')}{' '}
              <Link to="/privacy">{t('subscription.checkout.privacy')}</Link>.
            </p>

            <div className="checkout-cancel-info">
              <FaCheck aria-hidden />
              <p>{t('subscription.checkout.cancelInfo')}</p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function PlanCard({
  plan,
  price,
  period,
  priceSuffixKey,
  t,
}: {
  plan: Plan;
  price: number;
  period: BillingPeriod;
  priceSuffixKey: string;
  t: (key: string) => string;
}) {
  return (
    <div className="checkout-plan-card">
      <div className="checkout-plan-card-head">
        <PlanIcon type={plan.icon} className="checkout-plan-icon" />
        <div>
          <span className="checkout-plan-name">{t(plan.nameKey)}</span>
          <span className="checkout-plan-price">
            {formatPrice(price)} ₽ {t(priceSuffixKey)}
          </span>
        </div>
      </div>
      <ul className="checkout-plan-features">
        {plan.features
          .filter((f) => f.included)
          .map((feature) => (
            <li key={feature.key}>
              <FaCheck aria-hidden />
              {t(feature.key)}
            </li>
          ))}
      </ul>
      <span className="checkout-plan-period-badge">
        {t(`subscription.periods.${period}`)}
      </span>
    </div>
  );
}

export default SubscriptionCheckoutPage;
